import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { setTokenRefresher } from '../utils/authStorage';

export interface UserAuthData {
  user: Record<string, unknown> | undefined;
  accessToken: string | null;
  idToken: string | null;
  roles: string[];
  isLoading: boolean;
}

/**
 * Hook to fetch and store user authentication data
 * Automatically saves to localStorage for persistence
 *
 * Usage:
 * ```typescript
 * const { user, accessToken, roles, isLoading } = useUserAuth();
 *
 * if (isLoading) return <Loading />;
 *
 * return <div>Welcome, {user?.name}!</div>;
 * ```
 */
export const useUserAuth = (rolesNamespace?: string): UserAuthData => {
  const { user, isAuthenticated, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTokensAndRoles = async () => {
      // Skip if not authenticated
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      if (isAuthenticated) {
        try {
          // Register the ID token refresher so API interceptors can fetch fresh tokens.
          // We use getIdTokenClaims().__raw (ID token) because the backend validates
          // ID tokens in the Authorization header, not access tokens.
          setTokenRefresher(async () => {
            await getAccessTokenSilently({ cacheMode: 'on' }); // triggers silent refresh if expired
            const freshClaims = await getIdTokenClaims();
            return freshClaims?.__raw ?? '';
          });

          // Get access token
          const token = await getAccessTokenSilently({
            cacheMode: 'on',
          });
          setAccessToken(token);

          // Get ID token with claims
          const claims = await getIdTokenClaims();
          const rawIdToken = claims?.__raw || null;
          setIdToken(rawIdToken);

          // Extract roles from claims
          // Check for user-roles claim first, then try custom namespace and other common locations
          const namespace = rolesNamespace || 'https://lucid-partner-portal.com/roles';
          const userRoles =
            claims?.['user-roles'] ||
            user?.['user-roles'] ||
            claims?.[namespace] ||
            claims?.['http://lucid-partner-portal.com/roles'] ||
            claims?.roles ||
            user?.roles ||
            [];
          setRoles(Array.isArray(userRoles) ? userRoles : []);

          // Store in localStorage
          localStorage.setItem('accessToken', token);
          if (rawIdToken) {
            localStorage.setItem('idToken', rawIdToken);
          }
          localStorage.setItem('userRoles', JSON.stringify(userRoles));
          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('isAuthenticated', 'true');
          // Store org_id explicitly from ID token claims so it is reliably
          // available for the X-Org-Id header before /me resolves
          const orgId = (claims?.org_id as string) || null;
          if (orgId) {
            localStorage.setItem('orgId', orgId);
          }

          // Note: /me endpoint is called separately by UserIdentityProvider
          // This separation of concerns keeps auth logic separate from user identity logic
          // See: UserIdentityProvider -> useUserIdentitySync -> useGetUserIdentity
        } catch (error) {
          console.error('Error fetching tokens:', error);
          // Don't throw, just log and set loading to false
          // Clear localStorage on error
          localStorage.removeItem('accessToken');
          localStorage.removeItem('idToken');
          localStorage.removeItem('userRoles');
          localStorage.removeItem('user');
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('orgId');
          // Set empty values instead of throwing
          setAccessToken(null);
          setIdToken(null);
          setRoles([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Clear localStorage when not authenticated
        localStorage.removeItem('accessToken');
        localStorage.removeItem('idToken');
        localStorage.removeItem('userRoles');
        localStorage.removeItem('user');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('orgId');
        setIsLoading(false);
      }
    };

    fetchTokensAndRoles();
  }, [isAuthenticated, getAccessTokenSilently, getIdTokenClaims, user, rolesNamespace]);

  return {
    user,
    accessToken,
    idToken,
    roles,
    isLoading,
  };
};
